import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CreditCard, Search, Trash2, CheckSquare, Square, ChevronLeft, ChevronRight, AlertTriangle, Filter, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [page, statusFilter]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("admin_token");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20"
      });
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/transactions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTransactions(response.data.payments || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setSelectedIds(new Set());
      setSelectAll(false);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat transaksi (unauthorized).");
      } else {
        toast.error("Gagal memuat transaksi");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTransactions();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      confirmed: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700",
    };
    const labels = {
      pending: "Pending",
      confirmed: "Lunas",
      rejected: "Ditolak",
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>{labels[status]}</span>;
  };

  // Selection handlers
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === transactions.length);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(transactions.map(t => t.id)));
      setSelectAll(true);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error("Pilih transaksi yang ingin dihapus");
      return;
    }

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/transactions/delete`, {
        payment_ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setSelectedIds(new Set());
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus transaksi");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout title="Kelola Transaksi">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Search & Filter */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Cari No. Sambungan atau Nama..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-11 pl-11"
                  data-testid="search-input"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm"
                data-testid="status-filter"
              >
                <option value="">Semua Status</option>
                <option value="confirmed">Lunas</option>
                <option value="pending">Pending</option>
                <option value="rejected">Ditolak</option>
              </select>
              <Button type="submit" className="h-11 px-6" data-testid="search-btn">
                <Search className="w-4 h-4 mr-2" />
                Cari
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Actions Bar */}
        {selectedIds.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between"
          >
            <p className="text-red-700 font-medium">
              {selectedIds.size} transaksi dipilih
            </p>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="gap-2"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4" />
              Hapus Terpilih
            </Button>
          </motion.div>
        )}

        {/* Transactions Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Daftar Transaksi ({total})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                  Tidak Ada Transaksi
                </h3>
                <p className="text-slate-600">
                  Belum ada transaksi yang ditemukan
                </p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="data-table" data-testid="transactions-table">
                    <thead>
                      <tr>
                        <th className="w-12">
                          <button
                            onClick={toggleSelectAll}
                            className="p-1 hover:bg-slate-100 rounded"
                            data-testid="select-all-btn"
                          >
                            {selectAll ? (
                              <CheckSquare className="w-5 h-5 text-sky-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                        </th>
                        <th>No. Sambungan</th>
                        <th>Nama</th>
                        <th>Periode</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Tanggal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr 
                          key={tx.id} 
                          className={selectedIds.has(tx.id) ? "bg-sky-50" : ""}
                        >
                          <td>
                            <button
                              onClick={() => toggleSelect(tx.id)}
                              className="p-1 hover:bg-slate-100 rounded"
                              data-testid={`select-${tx.id}`}
                            >
                              {selectedIds.has(tx.id) ? (
                                <CheckSquare className="w-5 h-5 text-sky-600" />
                              ) : (
                                <Square className="w-5 h-5 text-slate-400" />
                              )}
                            </button>
                          </td>
                          <td className="font-mono">{tx.no_samb}</td>
                          <td>{tx.nama}</td>
                          <td className="text-sm">{tx.periode_list || tx.bulan || '-'}</td>
                          <td className="text-right font-medium text-sky-600">
                            {formatCurrency(tx.total_bayar)}
                          </td>
                          <td>{getStatusBadge(tx.status)}</td>
                          <td className="text-sm text-slate-500">
                            {tx.submitted_at ? formatDate(tx.submitted_at) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Halaman {page} dari {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Hapus {selectedIds.size} Transaksi?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Transaksi yang dipilih akan dihapus secara permanen dan status tagihan terkait akan di-reset ke "belum dibayar".</p>
                <p className="font-medium text-emerald-600">
                  ✓ Saldo akan dikembalikan ke kasir (agen/petugas) yang melakukan transaksi.
                </p>
                <p className="text-red-600 text-sm">Tindakan ini tidak dapat dibatalkan.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Batal</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSelected}
                className="bg-red-600 hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? <div className="spinner w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Ya, Hapus & Kembalikan Saldo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </AdminLayout>
  );
}
